import React, { useMemo } from 'react';
import { Select, type SelectProps } from '../Select';

/** Same synthetic event shape as {@link SelectProps.onChange}. */
export type LookupSelectChangeEvent = {
  target: { name?: string; value: string; type: string };
};

export type LookupSelectProps = Omit<SelectProps, 'options' | 'onChange'> & {
  /**
   * Ids that must remain visible even if inactive (typically the current form value),
   * so edit screens still show the selected row.
   */
  includeIds?: Array<string | number | null | undefined>;
  onChange?: (event: LookupSelectChangeEvent) => void;
};

type LookupSelectViewProps = LookupSelectProps & {
  options: SelectProps['options'];
  isLoading?: boolean;
};

/**
 * Thin wrapper around {@link Select} so all lookup dropdowns share one style and behavior.
 */
export function LookupSelect({
  options,
  isLoading = false,
  disabled,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  showClearButton = true,
  includeIds: _includeIds,
  ...rest
}: LookupSelectViewProps) {
  const mergedDisabled = Boolean(disabled) || isLoading;
  const safeOptions = useMemo(() => options ?? [], [options]);

  return (
    <Select
      {...rest}
      options={safeOptions}
      disabled={mergedDisabled}
      placeholder={isLoading ? 'Loading…' : placeholder}
      searchPlaceholder={searchPlaceholder}
      showClearButton={showClearButton}
    />
  );
}

export default LookupSelect;
